import React, { useState } from "react";
import Layout from "../../../components/Layout";
import Container from "../../../components/Container";
import { useHistory } from "react-router-dom";
import Flex from "../../../components/Flex";
import { useAuthState } from "../../../components/AuthProvider";
import Content from "../../../components/Content";
import Spinner from "../../../components/Loader/Spinner";
import ButtonList from "../../../components/ButtonList";
import LinkButton from "../../../components/LinkButton";
import DataTable from "../../../components/DataTable";
import DefaultBreadcrumbs from "../../../components/DefaultBreadcrumbs";
import { useMessageToast } from "../../../components/MessageProvider";
import Modal from "../../../components/ModalPopup";
import Button from "../../../components/Button";
import styles from "./SystemNotifications.module.scss";

const SystemNotifications = () => {
  const clientConfigApiDomain = process.env.REACT_APP_CLIENT_CONFIG_API_DOMAIN;
  const systemNotificationApiUrl = `${clientConfigApiDomain}/system_notifications`;

  const history = useHistory();
  const { user } = useAuthState();

  const [systemNotificationData, setSystemNotificationData] = useState([]);
  const [showDeleteDialog, setShowDeleteDialog] = React.useState(false);
  const toast = useMessageToast();
  const [
    selectedSystemNotification,
    setSelectedSystemNotification
  ] = React.useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);

  const clickableCell = ({
    cell: {
      value,
      row: { original: selectedSystemNotification }
    }
  }) => {
    const onClick = () => {
      history.push({
        pathname: `/admin/systemnotifications/${selectedSystemNotification.id}`
      });
    };

    return (
      <span className={styles.cell} onClick={onClick}>
        {value}
      </span>
    );
  };

  const handleDeleteSuccess = systemNotification => {
    toast.success(`Successfully deleted System Notification.`);
    setSystemNotificationData(
      systemNotificationData.filter(s => s !== systemNotification)
    );
  };

  const handleDeleteError = systemNotification => {
    toast.error(
      `Error when attempting to delete System Notification : ${systemNotification.id}.`
    );
  };

  const onDeleteCancelClick = () => {
    setShowDeleteDialog(false);
    setSelectedSystemNotification({});
  };

  const onDeleteSystemNotification = () => {
    fetch(`${systemNotificationApiUrl}/${selectedSystemNotification.id}/`, {
      method: "delete"
    })
      .then(response => {
        if (!response.ok) throw Error("Failed to delete System Notification");
        handleDeleteSuccess(selectedSystemNotification);
      })
      .catch(() => {
        handleDeleteError(selectedSystemNotification);
      });
    setShowDeleteDialog(false);
  };

  React.useEffect(() => {
    const fetchSystemNotifications = async () => {
      const response = await fetch(`${systemNotificationApiUrl}/`);
      const data = await response.json();

      if (data.hasOwnProperty("results")) {
        setSystemNotificationData(data.results);
      }
    };

    if (!hasLoaded) {
      setIsLoading(true);
      fetchSystemNotifications();
      setIsLoading(false);
      setHasLoaded(true);
    }
  }, [hasLoaded, systemNotificationApiUrl]);

  const actionCell = ({
    cell: {
      value,
      row: { original: selectedSystemNotification }
    }
  }) => {
    const onDetailsClick = () => {
      history.push(
        `/admin/systemnotifications/${selectedSystemNotification.id}`
      );
    };

    const onEditClick = () => {
      history.push(
        `/admin/systemnotifications/${selectedSystemNotification.id}/edit`
      );
    };

    const onDeleteClick = () => {
      setSelectedSystemNotification(selectedSystemNotification);
      setShowDeleteDialog(true);
    };

    return (
      <ButtonList className={styles.icons}>
        <LinkButton icon="view" onClick={onDetailsClick}>
          Details
        </LinkButton>
        <LinkButton icon="edit" onClick={onEditClick}>
          Edit
        </LinkButton>
        <LinkButton icon="remove" onClick={onDeleteClick}>
          Delete
        </LinkButton>
      </ButtonList>
    );
  };

  const columns = [
    {
      Header: "Message",
      accessor: "message",
      Cell: clickableCell
    },
    {
      Header: "Start Date",
      accessor: "start_date"
    },
    {
      Header: "End Date",
      accessor: "end_date"
    },
    {
      Header: "",
      accessor: "actions",
      disableFilters: true,
      disableSortBy: true,
      Cell: actionCell
    }
  ];

  return (
    <Layout>
      <Container>
        <Flex gutter={3}>
          <Flex.Item span={12}>
            <DefaultBreadcrumbs />
          </Flex.Item>
          <Flex.Item span={12}>
            <Content>
              <h1>System Notifications</h1>
              <p>
                Select a System Notifications to manage its respective
                properties.
              </p>
            </Content>
            <div className={styles.base}>
              <ButtonList spacing={2} style={{ margin: "1rem 0" }}>
                {user.role === "Global Admin" ? (
                  <LinkButton
                    icon="add"
                    onClick={() => {
                      history.push("/admin/systemnotifications/add");
                    }}
                  >
                    Add System Notifications
                  </LinkButton>
                ) : null}
              </ButtonList>
              {isLoading ? (
                <Spinner />
              ) : hasLoaded ? (
                <DataTable
                  columns={columns}
                  data={systemNotificationData}
                  hasSorting
                  hasPagination
                />
              ) : null}
            </div>
          </Flex.Item>
        </Flex>
      </Container>
      <Modal open={showDeleteDialog} onClose={() => setShowDeleteDialog(false)}>
        <div style={{ marginBottom: "1rem" }}>
          Are you sure you want to delete System Notification:{" "}
          {selectedSystemNotification.message}?
        </div>
        <ButtonList>
          <Button primary type="submit" onClick={onDeleteSystemNotification}>
            Confirm
          </Button>
          <Button onClick={onDeleteCancelClick}>Cancel</Button>
        </ButtonList>
      </Modal>
    </Layout>
  );
};

export default SystemNotifications;
